import { useEffect, useState, useRef, useCallback } from 'react';

const APPLIANCES = [
  { icon: 'ri-loader-2-line', name: 'Стиральные машины', desc: 'Все марки и модели', price: 'от 5 000 ₸' },
  { icon: 'ri-drop-line', name: 'Посудомоечные машины', desc: 'Быстрый ремонт', price: 'от 6 000 ₸' },
  { icon: 'ri-windy-line', name: 'Сушильные машины', desc: 'Профессионально', price: 'от 7 000 ₸' },
];

const FEATURES = [
  { icon: 'ri-shield-check-fill', text: 'Гарантия 1 год' },
  { icon: 'ri-timer-flash-fill', text: 'Выезд за 30 минут' },
  { icon: 'ri-tools-fill', text: 'Оригинальные запчасти' },
  { icon: 'ri-money-dollar-circle-fill', text: 'Оплата после ремонта' },
];

function useMouseParallax() {
  const [pos, setPos] = useState({ x: 0, y: 0 });

  const handleMove = useCallback((e: MouseEvent) => {
    if (typeof window === 'undefined' || window.innerWidth < 768) return;
    const cx = window.innerWidth / 2;
    const cy = window.innerHeight / 2;
    setPos({ x: (e.clientX - cx) / cx, y: (e.clientY - cy) / cy });
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.addEventListener('mousemove', handleMove);
    return () => window.removeEventListener('mousemove', handleMove);
  }, [handleMove]);

  return pos;
}

function AnimatedCounter({ target, suffix = '+' }: { target: number; suffix?: string }) {
  const [val, setVal] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const started = useRef(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started.current) {
          started.current = true;
          let step = 0;
          const steps = 60;
          const interval = setInterval(() => {
            step++;
            setVal(Math.floor(target * (step / steps)));
            if (step >= steps) {
              clearInterval(interval);
              setVal(target);
            }
          }, 2000 / steps);
        }
      },
      { threshold: 0.5 }
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [target]);

  return (
    <div ref={ref}>
      {val}
      {suffix}
    </div>
  );
}

function Particles() {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none hidden md:block">
      {Array.from({ length: 20 }).map((_, i) => (
        <div
          key={i}
          className="absolute rounded-full"
          style={{
            width: `${2 + Math.random() * 4}px`,
            height: `${2 + Math.random() * 4}px`,
            background: `rgba(255,255,255,${0.15 + Math.random() * 0.25})`,
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            animation: `particleFloat ${4 + Math.random() * 6}s ease-in-out infinite`,
            animationDelay: `${Math.random() * 5}s`,
          }}
        />
      ))}
    </div>
  );
}

function ApplianceCard({
  item,
  index,
  mouse,
  loaded,
}: {
  item: typeof APPLIANCES[0];
  index: number;
  mouse: { x: number; y: number };
  loaded: boolean;
}) {
  const [hovered, setHovered] = useState(false);
  const offsetX = mouse.x * (8 + index * 3);
  const offsetY = mouse.y * (5 + index * 2);

  return (
    <div
      className={`group relative cursor-pointer transition-all duration-700 ${
        loaded ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-20'
      }`}
      style={{
        transitionDelay: `${400 + index * 150}ms`,
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div
        style={{
          transform: `translate(${offsetX}px, ${offsetY}px)`,
          transition: 'transform 0.3s ease-out',
        }}
      >
        <div
          className={`relative bg-white/10 backdrop-blur-xl rounded-2xl p-5 md:p-7 border border-white/20 transition-all duration-500 overflow-hidden ${
            hovered ? 'bg-white/20 scale-105 shadow-2xl shadow-teal-500/20' : ''
          }`}
        >
          <div
            className={`absolute inset-0 bg-gradient-to-r from-transparent via-white/15 to-transparent skew-x-12 transition-transform duration-700 ${
              hovered ? 'translate-x-[200%]' : '-translate-x-[200%]'
            }`}
          />
          <div
            className={`absolute -inset-1 rounded-2xl bg-gradient-to-r from-teal-400 via-emerald-400 to-teal-400 opacity-0 blur-xl transition-opacity duration-500 ${
              hovered ? 'opacity-40' : ''
            }`}
          />
          <div className="relative z-10 flex items-center gap-4 md:flex-col md:items-start md:gap-0">
            <div
              className={`w-14 h-14 md:w-18 md:h-18 flex-shrink-0 rounded-xl md:rounded-2xl flex items-center justify-center transition-all duration-500 md:mb-4 ${
                hovered ? 'bg-gradient-to-br from-teal-400 to-emerald-500 rotate-6 scale-110' : 'bg-white/15'
              }`}
            >
              <i
                className={`${item.icon} text-2xl md:text-3xl text-white transition-transform duration-500 ${
                  hovered ? 'scale-110' : ''
                }`}
              ></i>
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-oswald text-base md:text-xl font-bold text-white mb-0.5 md:mb-2 uppercase tracking-wide">
                {item.name}
              </h3>
              <p className="text-teal-100 text-xs md:text-sm">{item.desc}</p>
              <div
                className={`overflow-hidden transition-all duration-500 ${
                  hovered ? 'max-h-20 opacity-100 mt-2' : 'max-h-0 opacity-0'
                }`}
              >
                <span className="inline-block bg-white/20 backdrop-blur-sm rounded-full px-3 py-1 text-xs md:text-sm font-semibold text-white whitespace-nowrap">
                  {item.price}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Hero() {
  const mouse = useMouseParallax();
  const [loaded, setLoaded] = useState(false);
  const [activeFeature, setActiveFeature] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => setLoaded(true), 100);
    const interval = setInterval(() => {
      setActiveFeature((prev) => (prev + 1) % FEATURES.length);
    }, 3000);
    return () => {
      clearTimeout(timer);
      clearInterval(interval);
    };
  }, []);

  const scrollToContact = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToServices = () => {
    document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="home" className="relative min-h-screen flex items-center overflow-hidden">
      {/* Background image — optimized for mobile */}
      <div className="absolute inset-0">
        {/* Desktop background */}
        <img
          src="https://readdy.ai/api/search-image?query=three%20modern%20white%20home%20appliances%20standing%20side%20by%20side%20in%20a%20row%20on%20a%20clean%20studio%20background%2C%20a%20front%20load%20washing%20machine%20on%20the%20left%2C%20a%20dishwasher%20in%20the%20center%20with%20door%20slightly%20open%2C%20and%20a%20tumble%20dryer%20on%20the%20right%2C%20professional%20product%20photography%2C%20soft%20studio%20lighting%2C%20minimalist%20clean%20light%20gray%20background%2C%20high%20resolution%20commercial%20photo%2C%20elegant%20composition%2C%20no%20text&width=1920&height=1080&seq=hero-appliances-trio-v9&orientation=landscape"
          alt="Стиральная, посудомоечная и сушильная машины"
          className="hidden md:block w-full h-full object-cover object-top"
          style={{
            transform: `scale(1.05) translate(${mouse.x * -6}px, ${mouse.y * -6}px)`,
            transition: 'transform 0.4s ease-out',
          }}
        />
        {/* Mobile background — portrait oriented, fits screen */}
        <img
          src="https://readdy.ai/api/search-image?query=three%20modern%20white%20home%20appliances%20arranged%20vertically%20in%20a%20portrait%20composition%20on%20clean%20studio%20background%2C%20front%20load%20washing%20machine%20dishwasher%20and%20tumble%20dryer%20stacked%20arrangement%2C%20professional%20product%20photography%20soft%20studio%20lighting%20minimalist%20clean%20light%20gray%20background%20high%20resolution%20commercial%20photo%20elegant%20vertical%20composition%20no%20text&width=750&height=1334&seq=hero-appliances-mobile-portrait-v3&orientation=portrait"
          alt="Стиральная, посудомоечная и сушильная машины"
          className="md:hidden w-full h-full object-cover object-center"
        />
      </div>

      {/* Overlays */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/85 via-black/70 to-black/90 md:from-black/75 md:via-black/55 md:to-black/80" />
      <div className="absolute inset-0 bg-gradient-to-r from-teal-900/30 via-transparent to-emerald-900/20" />

      {/* Animated gradient orbs */}
      <div className="absolute inset-0 opacity-25 hidden md:block">
        <div
          className="absolute w-[500px] h-[500px] rounded-full blur-[120px] animate-float"
          style={{
            background: 'radial-gradient(circle, rgba(20,184,166,0.35) 0%, transparent 70%)',
            top: '10%',
            left: '-5%',
            transform: `translate(${mouse.x * 15}px, ${mouse.y * 15}px)`,
            transition: 'transform 0.5s ease-out',
          }}
        />
        <div
          className="absolute w-[400px] h-[400px] rounded-full blur-[100px] animate-float"
          style={{
            background: 'radial-gradient(circle, rgba(16,185,129,0.25) 0%, transparent 70%)',
            bottom: '5%',
            right: '-5%',
            animationDelay: '1.5s',
            transform: `translate(${mouse.x * -10}px, ${mouse.y * -10}px)`,
            transition: 'transform 0.5s ease-out',
          }}
        />
      </div>

      <Particles />

      {/* Main content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 md:pt-28 pb-10 md:pb-16">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center min-h-[calc(100vh-180px)]">
          {/* Left side */}
          <div>
            {/* Badge */}
            <div
              className={`inline-flex items-center gap-2 bg-white/10 backdrop-blur-md rounded-full px-4 py-1.5 md:px-5 md:py-2 mb-6 md:mb-8 border border-white/20 transition-all duration-700 ${
                loaded ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-8'
              }`}
            >
              <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
              <span className="text-xs md:text-sm text-teal-100 font-medium">
                Работаем ежедневно с 8:00 до 22:00
              </span>
            </div>

            <h1
              className={`font-oswald text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-bold text-white leading-[1.05] mb-4 md:mb-6 uppercase tracking-tight transition-all duration-1000 ${
                loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
              }`}
              style={{ transitionDelay: '150ms' }}
            >
              <span className="block">Ремонт</span>
              <span className="block bg-gradient-to-r from-teal-300 via-emerald-300 to-teal-200 bg-clip-text text-transparent">
                бытовой техники
              </span>
              <span className="block text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-semibold text-white/80 mt-1 md:mt-2 normal-case tracking-normal">
                в Алматы
              </span>
            </h1>

            <p
              className={`text-base md:text-lg text-gray-300 mb-6 md:mb-8 max-w-lg leading-relaxed transition-all duration-700 ${
                loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
              style={{ transitionDelay: '350ms' }}
            >
              Профессиональный ремонт стиральных, посудомоечных и сушильных машин. Выезд мастера в день обращения с гарантией на все работы.
            </p>

            {/* CTA Buttons */}
            <div
              className={`flex flex-col sm:flex-row gap-3 md:gap-4 mb-8 md:mb-10 transition-all duration-700 ${
                loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
              style={{ transitionDelay: '500ms' }}
            >
              <button
                onClick={scrollToContact}
                className="group relative bg-gradient-to-r from-teal-500 to-emerald-500 text-white px-6 md:px-8 py-3.5 md:py-4 rounded-2xl text-base md:text-lg font-bold transition-all duration-300 shadow-xl shadow-teal-500/30 hover:shadow-2xl hover:shadow-teal-500/40 hover:scale-105 whitespace-nowrap overflow-hidden cursor-pointer font-oswald uppercase tracking-wider animate-glowPulse"
              >
                <span className="relative z-10 flex items-center justify-center gap-2 md:gap-3">
                  <i className="ri-calendar-check-fill text-lg md:text-xl"></i>
                  Вызвать мастера
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-emerald-500 to-teal-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="absolute inset-0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700 bg-gradient-to-r from-transparent via-white/20 to-transparent skew-x-12" />
              </button>

              <a
                href="tel:+77074325020"
                className="group flex items-center justify-center gap-2 md:gap-3 bg-white/10 backdrop-blur-md text-white px-6 md:px-8 py-3.5 md:py-4 rounded-2xl text-base md:text-lg font-bold border border-white/20 hover:bg-white/20 transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer"
              >
                <div className="w-9 h-9 md:w-10 md:h-10 bg-white/20 rounded-full flex items-center justify-center group-hover:bg-teal-500 transition-colors duration-300">
                  <i className="ri-phone-fill text-base md:text-lg"></i>
                </div>
                +7 (707) 432-50-20
              </a>
            </div>

            {/* Rotating features strip */}
            <div
              className={`relative h-11 md:h-12 overflow-hidden rounded-xl md:rounded-2xl bg-white/5 backdrop-blur-sm border border-white/10 transition-all duration-700 ${
                loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{ transitionDelay: '650ms' }}
            >
              {FEATURES.map((feat, i) => (
                <div
                  key={i}
                  className={`absolute inset-0 flex items-center justify-center gap-2 md:gap-3 transition-all duration-500 ${
                    activeFeature === i ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                  }`}
                >
                  <i className={`${feat.icon} text-teal-400 text-base md:text-lg`}></i>
                  <span className="text-white/90 text-xs md:text-sm font-medium">{feat.text}</span>
                </div>
              ))}
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-white/10">
                <div
                  className="h-full bg-gradient-to-r from-teal-400 to-emerald-400 transition-all duration-300"
                  style={{ width: `${((activeFeature + 1) / FEATURES.length) * 100}%` }}
                />
              </div>
            </div>
          </div>

          {/* Right side – cards */}
          <div className="flex flex-col gap-3 md:gap-5">
            {APPLIANCES.map((item, i) => (
              <ApplianceCard key={i} item={item} index={i} mouse={mouse} loaded={loaded} />
            ))}
          </div>
        </div>

        {/* Bottom stats bar */}
        <div
          className={`mt-6 md:mt-8 transition-all duration-1000 ${
            loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-16'
          }`}
          style={{ transitionDelay: '800ms' }}
        >
          <div className="bg-white/5 backdrop-blur-xl rounded-2xl md:rounded-3xl border border-white/10 p-4 md:p-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
              {[
                { target: 10, suffix: '+', label: 'Лет опыта' },
                { target: 5000, suffix: '+', label: 'Ремонтов' },
                { target: 98, suffix: '%', label: 'Довольных клиентов' },
                { target: 30, suffix: ' мин', label: 'Время выезда' },
              ].map((stat, i) => (
                <div key={i} className="text-center group cursor-default">
                  <div className="font-oswald text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-0.5 md:mb-1 group-hover:text-teal-300 transition-colors">
                    <AnimatedCounter target={stat.target} suffix={stat.suffix} />
                  </div>
                  <div className="text-xs md:text-sm text-gray-400">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="hidden md:flex justify-center mt-8">
          <button onClick={scrollToServices} className="group flex flex-col items-center gap-2 cursor-pointer">
            <span className="text-xs text-white/50 group-hover:text-white/80 transition-colors">
              Узнать больше
            </span>
            <div className="w-8 h-12 rounded-full border-2 border-white/30 flex justify-center pt-2 group-hover:border-teal-400 transition-colors">
              <div
                className="w-1.5 h-3 rounded-full animate-scrollBounce group-hover:bg-teal-400"
                style={{ backgroundColor: 'rgba(255,255,255,0.6)' }}
              />
            </div>
          </button>
        </div>
      </div>
    </section>
  );
}
